import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { ZoomIn, ZoomOut, RotateCw, Upload, Clipboard, X, Check } from 'lucide-react';
import { useTranslation } from '../lib/i18n';

// Helper function to create cropped image
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getRadianAngle = (degreeValue) => {
  return (degreeValue * Math.PI) / 180;
};

const rotateSize = (width, height, rotation) => {
  const rotRad = getRadianAngle(rotation);
  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
};

const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // Calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  );

  // Set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // Translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);

  // Draw rotated image
  ctx.drawImage(image, 0, 0);

  // Extract the cropped image
  const data = ctx.getImageData(
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height
  );

  // Set canvas width to final desired crop size - this will clear existing context
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  // Place the cropped data
  ctx.putImageData(data, 0, 0);

  // Return as blob
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        resolve(blob);
      },
      'image/jpeg',
      0.9
    );
  });
};

export const ImageCropper = ({ 
  open, 
  onClose, 
  onComplete, 
  aspectRatio = 1,
  title,
  cropShape = 'round' // 'round' or 'rect'
}) => {
  const { t } = useTranslation();
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [loading, setLoading] = useState(false);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let item of items) {
      if (item.type.indexOf('image') !== -1) {
        const blob = item.getAsFile();
        const reader = new FileReader();
        reader.addEventListener('load', () => {
          setImageSrc(reader.result);
        });
        reader.readAsDataURL(blob);
        break;
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer?.files;
    if (files && files.length > 0 && files[0].type.startsWith('image/')) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result);
      });
      reader.readAsDataURL(files[0]);
    }
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) {
      console.error('Missing imageSrc or croppedAreaPixels', { imageSrc: !!imageSrc, croppedAreaPixels });
      return;
    }
    
    setLoading(true);
    try {
      console.log('Starting crop with pixels:', croppedAreaPixels);
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      console.log('Cropped blob:', croppedBlob, 'size:', croppedBlob?.size);
      if (croppedBlob) {
        onComplete(croppedBlob);
      } else {
        console.error('Failed to create cropped blob');
      }
      handleReset();
      onClose();
    } catch (error) {
      console.error('Error cropping image:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title || t('imageCropper.title') || 'Resim Düzenle'}</DialogTitle>
        </DialogHeader>

        {!imageSrc ? (
          <div 
            className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-all"
            onPaste={handlePaste}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            tabIndex={0}
            data-testid="image-drop-zone"
          >
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
                <Upload className="h-8 w-8 text-slate-400" />
              </div>
              <div>
                <p className="text-lg font-medium text-slate-700">
                  {t('imageCropper.dragDrop') || 'Resmi sürükleyip bırakın'}
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  {t('imageCropper.or') || 'veya'}
                </p>
              </div>
              <div className="flex gap-3">
                <label className="cursor-pointer">
                  <input 
                    type="file" 
                    accept="image/*" 
                    className="hidden" 
                    onChange={handleFileSelect}
                    data-testid="image-file-input"
                  />
                  <Button type="button" variant="outline" className="pointer-events-none">
                    <Upload className="h-4 w-4 mr-2" />
                    {t('imageCropper.selectFile') || 'Dosya Seç'}
                  </Button>
                </label>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => navigator.clipboard.read().then(async (items) => {
                    for (const item of items) {
                      if (item.types.includes('image/png')) {
                        const blob = await item.getType('image/png');
                        const reader = new FileReader();
                        reader.addEventListener('load', () => setImageSrc(reader.result));
                        reader.readAsDataURL(blob);
                        break;
                      }
                    }
                  }).catch(() => {})}
                >
                  <Clipboard className="h-4 w-4 mr-2" />
                  {t('imageCropper.paste') || 'Yapıştır'}
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {t('imageCropper.pasteHint') || 'Ctrl+V ile panodan yapıştırabilirsiniz'}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Crop Area */}
            <div className="relative h-80 bg-slate-900 rounded-lg overflow-hidden">
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={aspectRatio}
                cropShape={cropShape}
                showGrid={false}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                restrictPosition={false}
                minZoom={0.5}
                maxZoom={3}
              />
            </div>

            {/* Controls */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
              {/* Zoom */}
              <div className="flex items-center gap-4">
                <ZoomOut className="h-4 w-4 text-slate-500" />
                <Slider
                  value={[zoom]}
                  min={0.5}
                  max={3}
                  step={0.1}
                  onValueChange={(value) => setZoom(value[0])}
                  className="flex-1"
                />
                <ZoomIn className="h-4 w-4 text-slate-500" />
              </div>

              {/* Rotation */}
              <div className="flex items-center gap-4">
                <RotateCw className="h-4 w-4 text-slate-500" />
                <Slider
                  value={[rotation]}
                  min={0}
                  max={360}
                  step={1}
                  onValueChange={(value) => setRotation(value[0])}
                  className="flex-1"
                />
                <span className="text-sm text-slate-500 w-12">{rotation}°</span>
              </div>
            </div>

            {/* Change Image Button */}
            <Button 
              type="button" 
              variant="outline" 
              size="sm"
              onClick={handleReset}
              className="w-full"
            >
              <X className="h-4 w-4 mr-2" />
              {t('imageCropper.changeImage') || 'Resmi Değiştir'}
            </Button>
          </div>
        )}

        <DialogFooter className="sticky bottom-0 bg-white dark:bg-slate-950 pt-3 -mx-6 px-6 pb-1 border-t">
          <Button variant="outline" onClick={handleClose} data-testid="image-cropper-cancel">
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={!imageSrc || loading}
            autoFocus
            size="lg"
            className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 shadow-lg"
            data-testid="image-cropper-save"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                {t('common.processing') || 'İşleniyor...'}
              </span>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                {t('common.save')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropper;
